import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { X, Download } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    // 检查是否已经是PWA模式
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);

    // 检查是否是iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // 检查是否已经提示过（24小时内不再提示）
    const lastPrompt = localStorage.getItem('pwa-prompt-dismissed');
    if (lastPrompt) {
      const lastTime = parseInt(lastPrompt, 10);
      if (Date.now() - lastTime < 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // 监听beforeinstallprompt事件（Chrome, Edge等）
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // 延迟显示提示，让用户先浏览网站
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // iOS上没有beforeinstallprompt事件，需要手动检测
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('PWA安装结果:', outcome);
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  // 已经是PWA模式或不显示提示
  if (isStandalone || !showPrompt) return null;

  return (
    <div className="fixed bottom-24 left-4 right-4 md:left-auto md:right-6 md:w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 z-50 animate-in slide-in-from-bottom-5">
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
          <img 
            src="/logo.jpeg" 
            alt="ArtCanvas Logo" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm">
            {t('pwa.installTitle', '安装素食星球App')}
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            {t('pwa.installDesc', '添加到主屏幕，随时随地访问')}
          </p>
        </div>
      </div>

      {isIOS ? (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
          <p className="font-medium mb-1">{t('pwa.iosInstructions', 'iOS安装步骤：')}</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>{t('pwa.iosStep1', '点击底部分享按钮')}</li>
            <li>{t('pwa.iosStep2', '选择"添加到主屏幕"')}</li>
          </ol>
        </div>
      ) : (
        <Button 
          onClick={handleInstall}
          className="w-full mt-3 bg-green-600 hover:bg-green-700"
          size="sm"
        >
          <Download className="w-4 h-4 mr-2" />
          {t('pwa.installButton', '立即安装')}
        </Button>
      )}
    </div>
  );
};

export default PWAInstallPrompt;
